import type { ReactElement } from "react";

import {
  acreageTierRows, acreageUsedLines, complexLotLine, CONTEXT_ONLY, NOT_PRICED,
  type AcreageBlock,
} from "./compCriteria";

/**
 * R7 ACREAGE ESTATE SEARCH (2026-10-31) — the notice that sits over the comps when the server ran
 * acreage mode. It states the values the automatic run used (each editable in the search options),
 * the three location tiers, how many condo or complex lots were left out, and that nothing here is
 * priced: a rate we have no support for reads "not priced", never $0. Display only; this component
 * does no arithmetic on any figure (the tier counts, acreage and sums come from the server).
 * A summary with no `acreage` block never reaches this component.
 */
export function AcreageNotice({ acreage }: { acreage: AcreageBlock }): ReactElement {
  const excluded = complexLotLine(acreage.excluded_complex_lots);
  return (
    <div className="vb-cs-acreage" data-testid="acreage-notice" role="group" aria-label="Acreage estate search">
      <p className="vb-cs-note" style={{ fontWeight: 600 }}>Acreage estate search</p>
      <p className="vb-cs-note">
        {acreage.agent_picked ? "You chose acreage mode." : "This site is 2 acres or more, so acreage mode is on."}
        {" "}These values replace the usual lot and living-area ranges. Change any of them in Adjust search.
      </p>
      {acreage.subject_acres != null && (
        <p className="vb-cs-why" data-testid="acreage-subject">
          This home&apos;s site: {acreage.subject_acres.toLocaleString("en-US", { maximumFractionDigits: 2 })} acres
          {acreage.subject_acres_tag ? <> <span className="vb-tag">{acreage.subject_acres_tag}</span></> : null}
          {acreage.subject_acres_how ? <> ({acreage.subject_acres_how})</> : null}
        </p>
      )}
      <ul className="vb-cs-counts" aria-label="Values this search used" data-testid="acreage-used">
        {acreageUsedLines(acreage).map((l) => <li key={l}>{l}</li>)}
      </ul>
      <ul className="vb-cs-counts" aria-label="Location tiers" data-testid="acreage-tiers">
        {acreageTierRows(acreage).map((t) => (
          <li key={t.tier} data-tier={t.tier}>
            {t.label}: {t.count} {t.count === 1 ? "sale" : "sales"}{t.note ? ` — ${t.note}` : ""}
          </li>
        ))}
      </ul>
      {excluded && <p className="vb-cs-note" data-testid="acreage-excluded">{excluded}</p>}
      <p className="vb-cs-note" data-testid="acreage-context">
        Active and pending listings and land listings are {CONTEXT_ONLY}. Excess-acre and land rates are {NOT_PRICED}.
      </p>
      {acreage.notes.filter((n) => /fewer than|No range was widened|state no distance/.test(n)).map((n) => (
        <p key={n} className="vb-cs-note">{n}</p>
      ))}
    </div>
  );
}
